import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PackageSearchForm } from './PackageSearchForm';
import { PackageResults } from './PackageResults';
import { PackageDetails } from './PackageDetails';
import { PackageCheckout } from './PackageCheckout';
import { FlexDates } from './FlexDates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UmrahPackages = ({ user }) => {
  const [stage, setStage] = useState('search');
  const [params, setParams] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('package') || sessionStorage.getItem('umrah_shared_package');
    if (!id) return;
    sessionStorage.removeItem('umrah_shared_package');
    (async () => {
      setLoading(true);
      try {
        const { data: pkg } = await axios.get(`${API_URL}/api/umrah/packages/${id}`);
        setData({ count: 1, packages: [pkg], availability: {}, dates: { departure: pkg.flight.outbound.departure_date, return: pkg.flight.inbound.departure_date }, shared: true });
        setStage('results'); setDetails(pkg);
      } catch (e) { setError(e.response?.data?.detail || 'This shared package has expired — please run a new search.'); }
      finally { setLoading(false); }
    })();
  }, []);
  const search = async (p) => {
    setLoading(true); setError(null); setParams(p);
    try {
      const { data: res } = await axios.post(`${API_URL}/api/umrah/packages/search`, p);
      setData(res); setStage('results'); window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setError(e.response?.data?.detail || 'Package search is unavailable right now. Please try again shortly.'); }
    finally { setLoading(false); }
  };
  const updated = (pkg) => {
    setData((d) => ({ ...d, packages: d.packages.map((p) => (p.id === pkg.id ? pkg : p)) }));
    setDetails((cur) => (cur && cur.id === pkg.id ? pkg : cur));
  };
  const select = (pkg) => { setSelected({ ...pkg, dates_return: data.dates.return }); setDetails(null); setStage('checkout'); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div className="max-w-6xl mx-auto sm:px-4" data-testid="umrah-packages">
      {stage !== 'search' && <Button variant="ghost" onClick={() => setStage(stage === 'checkout' ? 'results' : 'search')} className="mb-3" data-testid="pkg-back-btn"><ArrowLeft className="h-4 w-4 mr-2" />{stage === 'checkout' ? 'Back to packages' : 'Modify search'}</Button>}
      {stage === 'search' && (
        <>
          <PackageSearchForm initial={params} onSearch={search} loading={loading} />
          {error && <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4 text-red-700 font-semibold" data-testid="pkg-search-error">{error}</div>}
        </>
      )}
      {loading && stage !== 'search' && <div className="py-16 text-center text-slate-600"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#FF6600] mb-2" />Refreshing packages…</div>}
      {stage === 'results' && data && !loading && (
        data.count === 0
          ? <div className="rounded-2xl border border-dashed bg-white p-10 text-center" data-testid="pkg-no-results"><p className="font-semibold text-slate-900">No complete packages are available for these dates.</p><p className="text-sm text-slate-500 mt-1">{Object.entries(data.availability).map(([s, a]) => a.reason ? `${s}★: ${a.reason.replace(/_/g, ' ')}` : null).filter(Boolean).join(' · ')}</p><Button variant="outline" className="mt-4" onClick={() => setStage('search')}>Modify search</Button></div>
          : <>
              {params && <div className="mb-4"><FlexDates params={params} currentCheapest={Math.min(...data.packages.map((p) => p.price.per_person))} loading={loading} onPick={(d) => search({ ...params, departure_date: d })} /></div>}
              <PackageResults data={data} onDetails={setDetails} onSelect={select} />
            </>
      )}
      {stage === 'checkout' && selected && <PackageCheckout pkg={selected} user={user} />}
      <PackageDetails pkg={details} open={!!details} onClose={() => setDetails(null)} onUpdated={updated} onSelect={select} />
    </div>
  );
};
